import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Clock, TrendingUp, PiggyBank, Home, Calculator, Shield } from 'lucide-react';
import type { MortgageData } from '../../CalculatorTabs';
import { formatCurrency } from '@/components/calculator/utils/formatters';

interface PersonalizedFinancialGoalsProps {
  mortgageData: MortgageData;
  selectedTerm: string;
}

export const PersonalizedFinancialGoals: React.FC<PersonalizedFinancialGoalsProps> = ({ 
  mortgageData, 
  selectedTerm 
}) => {
  // Estimate financial data from calculator inputs
  const estimatedMonthlyIncome = mortgageData.monthlyPayment.total / 0.28;
  const estimatedTotalMonthlyDebt = mortgageData.monthlyPayment.total * 0.15;
  const creditScore = mortgageData.creditScore || 720;
  const state = mortgageData.state || 'CA';
  const loanType = mortgageData.loanType || 'conventional';
  const maxBuyingPower = mortgageData.homePrice;
  const debtToIncomeRatio = (mortgageData.monthlyPayment.total + estimatedTotalMonthlyDebt) / estimatedMonthlyIncome;
  
  // Calculate personalized short-term goals based on user data
  const generateShortTermGoals = () => {
    const goals = [];
    const availableCashFlow = estimatedMonthlyIncome - estimatedTotalMonthlyDebt - mortgageData.monthlyPayment.total;
    
    // Insurance optimization
    const monthlyInsurance = mortgageData.homeInsurance / 12;
    goals.push({
      icon: <Shield className="w-4 h-4" />,
      text: `Review insurance rates - current ${formatCurrency(monthlyInsurance)}/month could potentially be reduced by 10-20%`
    });
    
    // Payment schedule optimization
    if (availableCashFlow > 200) {
      goals.push({
        icon: <Calculator className="w-4 h-4" />,
        text: `Consider bi-weekly payments - your ${formatCurrency(availableCashFlow)} monthly surplus supports this strategy`
      });
    }
    
    // Credit score monitoring
    if (creditScore < 760) {
      goals.push({
        icon: <TrendingUp className="w-4 h-4" />,
        text: `Focus on credit improvement from ${creditScore} to 760+ for better rates`
      });
    } else {
      goals.push({
        icon: <TrendingUp className="w-4 h-4" />,
        text: `Maintain excellent ${creditScore} credit score through on-time payments`
      });
    }
    
    // Property tax appeals for high-tax states
    if (['CA', 'NY', 'TX', 'NJ', 'IL'].includes(state)) {
      goals.push({
        icon: <Home className="w-4 h-4" />,
        text: `Property tax appeal in ${state} - potential savings on ${formatCurrency(mortgageData.propertyTax)}/month`
      });
    }
    
    return goals;
  };
  
  // Calculate personalized long-term goals
  const generateLongTermGoals = () => {
    const goals = [];
    const currentLTV = ((mortgageData.homePrice - mortgageData.downPayment) / mortgageData.homePrice) * 100;
    
    // Refinancing strategy
    if (creditScore >= 720) {
      goals.push({
        icon: <TrendingUp className="w-4 h-4" />,
        text: `Monitor rates for refinancing - your ${creditScore} score qualifies for premium rates`
      });
    } else {
      goals.push({
        icon: <TrendingUp className="w-4 h-4" />,
        text: `Improve credit to 720+ first, then plan refinancing strategy`
      });
    }
    
    // Equity building
    if (currentLTV > 80) {
      goals.push({
        icon: <Home className="w-4 h-4" />,
        text: `Build equity to 20%+ for PMI removal and better loan terms (currently ${Math.round(100 - currentLTV)}% equity)`
      });
    } else {
      goals.push({
        icon: <Home className="w-4 h-4" />,
        text: `Leverage ${Math.round(100 - currentLTV)}% equity for home improvements or investment opportunities`
      });
    }
    
    // Investment property consideration
    if (maxBuyingPower > mortgageData.homePrice * 1.5 && debtToIncomeRatio < 0.3) {
      goals.push({
        icon: <PiggyBank className="w-4 h-4" />,
        text: `Strong financial position supports investment property consideration`
      });
    }
    
    // Tax optimization
    if (loanType === 'conventional' && mortgageData.homePrice > 500000) {
      goals.push({
        icon: <Calculator className="w-4 h-4" />,
        text: `Optimize mortgage interest deduction strategies for high-value property`
      });
    } else {
      goals.push({
        icon: <Calculator className="w-4 h-4" />,
        text: `Maximize tax benefits through strategic mortgage and property tax planning`
      });
    }
    
    return goals;
  };
  
  const shortTermGoals = generateShortTermGoals();
  const longTermGoals = generateLongTermGoals();
  
  return (
    <Card className="bg-slate-900/60 border-slate-700">
      <CardHeader>
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-400" />
          Personalized Financial Strategy
        </CardTitle>
        <p className="text-slate-400 text-sm">
          Tailored recommendations based on your {formatCurrency(estimatedMonthlyIncome)}/month estimated income, 
          {Math.round(debtToIncomeRatio * 100)}% DTI, and {creditScore} credit score
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-blue-400" />
              <h4 className="font-semibold text-slate-200">Short-term Actions (3-12 months)</h4>
            </div>
            <ul className="space-y-3">
              {shortTermGoals.map((goal, index) => (
                <li key={index} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="text-blue-400 mt-0.5">{goal.icon}</span>
                  <span>{goal.text}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <h4 className="font-semibold text-slate-200">Long-term Strategies (1-5 years)</h4>
            </div>
            <ul className="space-y-3">
              {longTermGoals.map((goal, index) => (
                <li key={index} className="flex items-start gap-3 text-sm text-slate-300">
                  <span className="text-green-400 mt-0.5">{goal.icon}</span>
                  <span>{goal.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Financial Health Summary */}
        <div className="mt-6 pt-4 border-t border-slate-600">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h5 className="font-medium text-slate-200 mb-2">Your Financial Position Summary</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-slate-400">Monthly Income (Est.)</div>
                <div className="font-medium text-green-400">{formatCurrency(estimatedMonthlyIncome)}</div>
              </div>
              <div className="text-center">
                <div className="text-slate-400">Total DTI</div>
                <div className={`font-medium ${debtToIncomeRatio <= 0.36 ? 'text-green-400' : 'text-yellow-400'}`}>
                  {Math.round(debtToIncomeRatio * 100)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-400">Home Price</div>
                <div className="font-medium text-blue-400">{formatCurrency(maxBuyingPower)}</div>
              </div>
              <div className="text-center">
                <div className="text-slate-400">Cash Flow (Est.)</div>
                <div className={`font-medium ${(estimatedMonthlyIncome - estimatedTotalMonthlyDebt - mortgageData.monthlyPayment.total) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(Math.max(0, estimatedMonthlyIncome - estimatedTotalMonthlyDebt - mortgageData.monthlyPayment.total))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};